/**
 * The `composed-line` group style (05 §2.1), as one pure function.
 *
 * It reproduces `SpellDetail.buildSchoolLine` — `Evocation (Fire) [Fire,
 * Mind-Affecting]` — from the group's authored `render_config.pattern`, in
 * which `{key}` names a member and everything else is literal:
 *
 *     "{school} ({subschool}) [{descriptors}]"
 *     "{str}, {dex}, {con}, {int}, {wis}, {cha}, {ability_modifiers}"
 *
 * THE ONE RULE THAT MAKES A PATTERN WORK, and it had to be worked out rather
 * than assumed. A member with no value must take its decoration with it — a
 * spell with no subschool renders `Evocation [Fire]`, never `Evocation ()
 * [Fire]` — but the literal text around a placeholder is doing two DIFFERENT
 * jobs in the six patterns P2 authored, and the two behave oppositely when a
 * member is absent:
 *
 *   • a WRAPPER belongs to its member and disappears with it — the `(`/`)`
 *     around a subschool, the `[`/`]` around descriptors;
 *   • a SEPARATOR belongs to neither neighbour and appears only between two
 *     members that both rendered — the `, ` between six ability scores.
 *
 * Attaching every literal to a neighbour makes `+2 Str,` out of a race with no
 * Dex bonus; treating every literal as a separator makes `Evocation ( ) [Fire]`
 * out of a spell with no subschool. So the parse splits them: the trailing run
 * of OPENING brackets in the literal before a placeholder is that member's
 * prefix, the leading run of CLOSING brackets in the literal after it is its
 * suffix, and whatever is left over is the separator between them. That rule
 * reproduces all six authored patterns with nothing entity-specific in it.
 *
 * A `many` reference contributes its targets' names joined by ", " — the
 * descriptor list — so the pattern needs no per-member join parameter.
 *
 * The second authored composed-line shape (`parts`/`lines`/`separator`, used by
 * the monster family) is NOT implemented here: it lands with Monsters, where
 * there is a render to grade it against. See authoring call 92.
 */

const PLACEHOLDER = /\{([a-z_][a-z0-9_]*)\}/gi;
const OPENING = /[([{<]+$/;
const CLOSING = /^[)\]}>]+/;

/** The display text of one member's value, or '' when it contributes nothing. */
export function memberText(value) {
  if (value === undefined || value === null || value === '') return '';
  if (Array.isArray(value)) {
    return value.map((v) => (v && typeof v === 'object' ? v.name : v)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') return value.name || '';
  if (typeof value === 'boolean') return value ? 'Yes' : '';
  return String(value);
}

/** `pattern` → `[{ key, prefix, suffix, separator }]`, separator preceding each. */
export function parsePattern(pattern) {
  const raw = [];
  let last = 0;
  let match;
  PLACEHOLDER.lastIndex = 0;
  while ((match = PLACEHOLDER.exec(pattern)) !== null) {
    raw.push({ before: pattern.slice(last, match.index), key: match[1] });
    last = match.index + match[0].length;
  }
  const trailing = pattern.slice(last);

  return raw.map((item, i) => {
    const before = item.before;
    const after = i + 1 < raw.length ? raw[i + 1].before : trailing;
    const prefix = (before.match(OPENING) || [''])[0];
    const suffix = (after.match(CLOSING) || [''])[0];
    // The separator is what the previous member's suffix and this member's
    // prefix have both declined to claim.
    const previousSuffix = i === 0 ? '' : (before.match(CLOSING) || [''])[0];
    const middle = before.slice(previousSuffix.length, before.length - prefix.length);
    return { key: item.key, prefix, suffix, separator: middle };
  });
}

export default function composeLine(pattern, values) {
  if (!pattern) return '';
  const parts = parsePattern(pattern);

  let out = '';
  for (const part of parts) {
    const text = memberText(values[part.key]);
    if (!text) continue;
    if (out) out += part.separator || ' ';
    out += `${part.prefix}${text}${part.suffix}`;
  }
  return out.replace(/\s+/g, ' ').trim();
}
